import { DepositStatus } from '@dill/dill-shared';
import { Bank, CheckCircle, Spinner, XCircle } from 'phosphor-react';

/**
 * Whether the payments a post created have been grouped into a bank deposit in the ERP.
 *
 * WAITING is the state worth getting right: it is not a failure. The advice arrived before the money
 * — the normal order — so there is no bank credit to date the deposit from yet, and settlement makes
 * the deposit on its own once one lands. Coloured as information rather than as a problem, and
 * NOT_APPLICABLE renders as nothing at all, since a supplier who does not deposit through Dill has
 * no use for a badge on every row saying so.
 */
const statusConfig: Record<
  Exclude<DepositStatus, 'NOT_APPLICABLE'>,
  { label: string; title: string; icon: React.ReactNode; className: string }
> = {
  WAITING: {
    label: 'Awaiting deposit',
    title: 'Posted. Waiting for the bank deposit that dates it — this happens on its own.',
    icon: <Bank size={14} weight="fill" />,
    className: 'bg-gray-50 text-gray-600 ring-gray-500/20',
  },
  PENDING: {
    label: 'Depositing',
    title: 'The deposit is being created in the accounting system.',
    icon: <Spinner size={14} weight="bold" className="animate-spin" />,
    className: 'bg-blue-50 text-blue-700 ring-blue-600/20',
  },
  SUCCESSFUL: {
    label: 'Deposited',
    title: 'Deposited in the accounting system, dated the day the bank credited the money.',
    icon: <CheckCircle size={14} weight="fill" />,
    className: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
  },
  FAILED: {
    label: 'Deposit failed',
    title: 'The payments applied correctly, but they are still in Undeposited Funds.',
    icon: <XCircle size={14} weight="fill" />,
    className: 'bg-red-50 text-red-700 ring-red-600/20',
  },
};

interface DepositStatusBadgeProps {
  status: DepositStatus;
  /** Overrides the stock tooltip, for showing the recorded error on a failure. */
  title?: string | null;
}

export const DepositStatusBadge = ({ status, title }: DepositStatusBadgeProps) => {
  if (status === 'NOT_APPLICABLE') {
    return null;
  }
  const config = statusConfig[status];
  return (
    <span
      title={title || config.title}
      className={
        'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ring-1 ring-inset ' +
        config.className
      }>
      {config.icon}
      {config.label}
    </span>
  );
};
